import { lazy, useState, useEffect, useLayoutEffect, Suspense } from 'react'

import { createRoot } from 'react-dom/client'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { BrowserRouter as Router, Route } from 'react-router-dom'
import { useTransition, animated } from 'react-spring'
import smartOutline from 'smart-outline'
import { ThemeProvider } from 'styled-components'

import { gaInit } from 'analytics'
import { AudioProvider } from 'audioContext'
import { Loading } from 'components/Loading'
import { SmallPlayer } from 'components/Player'
import {
  IconEmbrace,
  IconPhotos,
  IconMicrophone,
  IconHeroes,
  IconHome,
} from 'components/icons'
import { AppContext } from 'context'
import { useBundle } from 'hooks/useBundle'
import { translations } from 'locales'
import RoutesComponent from 'routes'
import Settings from 'settings'
import { getLanguage, setLanguage } from 'storage'
import { GlobalStyle } from 'styles/global'
import { defaultTheme } from 'styles/theme'
import getEnv from 'utils/getEnv'
// import { lazyMinLoadTime } from 'utils/lazyLoadMinTime'
import { setupSettings } from 'utils/settings'
import { i18n, useTranslation } from 'utils/translation'
import { LoadingScreen } from 'views/loading'

import { Bundle } from './types/bundle'
// eslint-disable-next-line import/order
import registerServiceWorker from './serviceWorkerRegistration'

/* Will bring these in instead of lazy loads if we start to hit issues. */
// import About from './views/about'
// import AboutDetail from './views/about-detail'
// import Stories from './views/audio-stories'
// import AudioStory from './views/audio-story'
// import Heroes from './views/heroes'
// import Home from './views/home'
// import Essays from './views/photo-essays'

/**
 * Setup settings universally
 */
setupSettings(Settings)

i18n(translations, Settings.uiLanguages, Settings.defaultUILanguage)
smartOutline.init()

gaInit()

// While developing, slow down the suspense.
// const slowerSuspense = (path: string, slowBy: number) =>
//   lazyMinLoadTime(() => import(`${path}`), slowBy)

const Styleguide = lazy(() => import('./views/styleguide'))
const AudioStory = lazy(() => import('./views/audio-story'))
const Home = lazy(() => import('./views/home'))
const Stories = lazy(() => import('./views/audio-stories'))
const Essays = lazy(() => import('./views/photo-essays'))
const Heroes = lazy(() => import('./views/heroes'))
const About = lazy(() => import('./views/about'))
const AboutDetail = lazy(() => import('./views/about-detail'))

const NotFound = lazy(() => import('./views/not-found'))

const env = getEnv()
const routes = [
  {
    i18nKey: 'navigation.home',
    icon: IconHome,
    path: '/',
    element: <Home />,
  },
  {
    i18nKey: 'navigation.stories',
    icon: IconMicrophone,
    path: '/stories',
    element: (
      <Suspense fallback={<div className="pageLoad" />}>
        <Stories />
      </Suspense>
    ),
  },
  {
    i18nKey: 'navigation.heroes',
    icon: IconHeroes,
    path: '/heroes',
    element: (
      <Suspense fallback={<div className="pageLoad" />}>
        <Heroes />
      </Suspense>
    ),
  },
  {
    i18nKey: 'navigation.photo-stories',
    icon: IconPhotos,
    path: '/photo-stories',
    element: (
      <Suspense fallback={<div className="pageLoad" />}>
        <Essays />
      </Suspense>
    ),
    nested: {
      path: '/photo-stories/:photoStoryId',
      element: (
        <Suspense fallback={<div className="pageLoad" />}>
          <Essays />
        </Suspense>
      ),
    },
  },
  {
    path: '/stories/:audioStorySlug',
    element: (
      <Suspense fallback={<div className="pageLoad" />}>
        <AudioStory />
      </Suspense>
    ),
  },
  {
    i18nKey: 'navigation.about',
    icon: IconEmbrace,
    path: '/about',
    element: (
      <Suspense fallback={<div className="pageLoad" />}>
        <About />
      </Suspense>
    ),
  },
  {
    path: '/about/:aboutSlug',
    element: (
      <Suspense fallback={<div className="pageLoad" />}>
        <AboutDetail />
      </Suspense>
    ),
  },
]

export const App = () => {
  const {
    i18n: { language },
  } = useTranslation()
  const [bundleLoader, setBundleLoader] = useState<Bundle | null>(null)
  const [contentLoaded, setContentLoaded] = useState(false)
  const [loadFailure, setLoadFailure] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const { fullBundle } = useBundle()

  // Set Language to `en` if no language is selected`
  // This is temporary until content localization is in place.
  // For now language changes only change Story audio files and transcripts.
  if (!getLanguage()) setLanguage('en')

  const transitions = useTransition(!contentLoaded, {
    enter: { opacity: 1 },
    from: { opacity: 1 },
    leave: { opacity: 0 },
    config: {
      duration: 350,
    },
  })
  /**
   *
   * This functionality is in place to take care of vh measurements on iOS safari,
   * where the browser chrome-elements interrupt a typical measurement of 100vh
   */
  useLayoutEffect(() => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    const vh = window.innerHeight
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`)
    window.addEventListener('resize', () => {
      // We execute the same script as before
      const intVh = window.innerHeight
      document.documentElement.style.setProperty('--vh', `${intVh}px`)
    })
  }, [])

  useEffect(() => {
    async function setup() {
      try {
        const response = await fullBundle()
        setTimeout(() => {
          setBundleLoader(response)
          setContentLoaded(true)
        }, 3000)
      } catch (e) {
        setLoadFailure(true)
        console.error('Bundle setup failed', e)
      }
    }
    setup()
  }, [])

  return (
    <AppContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        bundleLoader,
        loadFailure,
        contentLoaded,
        settingsOpen,
        setSettingsOpen,
      }}
    >
      {transitions(
        (props, item) =>
          item && (
            <animated.div className="loading-screen-wrap" style={props}>
              <LoadingScreen loading={!contentLoaded} />
            </animated.div>
          )
      )}
      <AudioProvider>
        <ThemeProvider theme={defaultTheme}>
          <GlobalStyle />
          <HelmetProvider context={{}}>
            <Helmet
              // Set lang to the current UI language
              // N.B. content fields will be set to the appropriate content
              // language as required
              htmlAttributes={{ lang: language }}
              titleTemplate={`%s - ${env.APP_TITLE}`}
              defaultTitle={env.APP_TITLE}
            />
            <Suspense fallback={<Loading />}>
              <Router>
                <RoutesComponent routes={routes}>
                  <Route path="*" element={<NotFound />} />
                  {process.env.NODE_ENV !== 'production' ? (
                    <Route path="/styleguide" element={<Styleguide />} />
                  ) : null}
                </RoutesComponent>
                <SmallPlayer />
              </Router>
            </Suspense>
          </HelmetProvider>
        </ThemeProvider>
      </AudioProvider>
    </AppContext.Provider>
  )
}

const container = document.getElementById('root')

if (process.env.NODE_ENV === 'production') {
  console.log('service worker')
  registerServiceWorker()
}

if (container) {
  const root = createRoot(container)
  root.render(<App />)
}
